import { useState } from 'react';
import { ordersApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from './ui/collapsible';
import { toast } from 'sonner';
import { 
    Shield, 
    CheckCircle, 
    AlertTriangle, 
    Clock,
    Pencil,
    ChevronDown,
    ChevronRight,
    History,
    Mail,
} from 'lucide-react';
import { NCBReminderDialog } from './NCBReminderDialog';

// NCB Status constants
const NCB_STATUS = {
    PENDING: 'pending',
    CLEARED: 'cleared',
    FOLLOWUP: 'follow_up_required'
};

// Status badge component
const NCBStatusBadge = ({ status }) => {
    const config = {
        [NCB_STATUS.PENDING]: { 
            label: 'Pending', 
            icon: Clock, 
            className: 'bg-amber-100 text-amber-700 border-amber-200' 
        },
        [NCB_STATUS.CLEARED]: { 
            label: 'Cleared', 
            icon: CheckCircle, 
            className: 'bg-emerald-100 text-emerald-700 border-emerald-200' 
        },
        [NCB_STATUS.FOLLOWUP]: { 
            label: 'Follow-up Required', 
            icon: AlertTriangle, 
            className: 'bg-red-100 text-red-700 border-red-200' 
        },
    };
    
    const { label, icon: Icon, className } = config[status] || config[NCB_STATUS.PENDING];
    
    return (
        <Badge className={`${className} border font-medium`}>
            <Icon className="w-3 h-3 mr-1" />
            {label}
        </Badge>
    );
};

export function NCBClearanceSection({ order, onUpdate, embedded = false }) {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showAuditLog, setShowAuditLog] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
    
    // Form state
    const [status, setStatus] = useState(order.ncb_clearance?.status || NCB_STATUS.PENDING);
    const [reference, setReference] = useState(order.ncb_clearance?.reference || '');
    const [note, setNote] = useState(order.ncb_clearance?.note || '');
    
    const ncb = order.ncb_clearance || { status: NCB_STATUS.PENDING };
    const isCleared = ncb.status === NCB_STATUS.CLEARED;
    // NCB clearance can be edited at all times - only block production if not cleared and not in Draft
    const isBlocking = !isCleared && order.status !== 'Draft';
    
    const handleSave = async () => {
        setIsSaving(true);
        try {
            await ordersApi.updateNCBClearance(order.id, {
                status,
                reference: reference || null,
                note: note || null
            });
            toast.success('NCB clearance updated');
            setIsEditing(false);
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update NCB clearance');
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleCancel = () => {
        setStatus(ncb.status || NCB_STATUS.PENDING);
        setReference(ncb.reference || '');
        setNote(ncb.note || '');
        setIsEditing(false);
    };
    
    const loadAuditLogs = async () => {
        if (auditLogs.length > 0) {
            setShowAuditLog(!showAuditLog);
            return;
        }
        
        setLoadingLogs(true);
        try {
            const response = await ordersApi.getNCBAuditLogs(order.id);
            setAuditLogs(response.data);
            setShowAuditLog(true);
        } catch (error) {
            toast.error('Failed to load audit logs');
        } finally {
            setLoadingLogs(false);
        }
    };
    
    // Content shared between embedded and standalone modes
    const content = (
        <div data-testid="ncb-clearance-section">
            {/* Blocking warning */}
            {isBlocking && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium text-amber-800">Production Blocked</p>
                        <p className="text-xs text-amber-700">
                            NCB clearance is required before this order can proceed to production.
                        </p>
                    </div>
                </div>
            )}
            
            {isEditing ? (
                <div className="space-y-3 pt-2 border-t">
                    <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="ncb-status-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NCB_STATUS.PENDING}>
                                    <span className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-amber-500" />
                                        Pending
                                    </span>
                                </SelectItem>
                                <SelectItem value={NCB_STATUS.CLEARED}>
                                    <span className="flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                                        Cleared
                                    </span>
                                </SelectItem>
                                <SelectItem value={NCB_STATUS.FOLLOWUP}>
                                    <span className="flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4 text-red-500" />
                                        Follow-up Required
                                    </span>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Clearance Reference (optional)</Label>
                        <Input
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="e.g., NCB-2026-001234"
                            data-testid="ncb-reference-input"
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Internal Note (optional)</Label>
                        <Textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Add internal notes about the clearance..."
                            rows={2}
                            data-testid="ncb-note-input"
                        />
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                        <Button 
                            onClick={handleSave} 
                            disabled={isSaving}
                            className="bg-slate-700 hover:bg-slate-800"
                            data-testid="ncb-save-btn"
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-2 pt-2 border-t">
                    {ncb.reference && (
                        <div>
                            <p className="text-xs text-muted-foreground">Reference</p>
                            <p className="text-sm font-medium">{ncb.reference}</p>
                        </div>
                    )}
                    
                    {ncb.note && (
                        <div>
                            <p className="text-xs text-muted-foreground">Note</p>
                            <p className="text-sm text-slate-600">{ncb.note}</p>
                        </div>
                    )}
                    
                    {ncb.updated_at && (
                        <p className="text-xs text-muted-foreground">
                            Last updated by {ncb.updated_by} on {new Date(ncb.updated_at).toLocaleDateString()}
                        </p>
                    )}
                    
                    <div className="flex gap-2 pt-2 flex-wrap">
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setIsEditing(true)}
                            data-testid="ncb-edit-btn"
                        >
                            <Pencil className="h-4 w-4 mr-1" />
                            Update
                        </Button>
                        {/* Admin-only Send Reminder — only when NCB is not
                            cleared AND the order has moved past Draft.
                            Opens the preview-and-edit dialog; it never
                            sends on its own. */}
                        {!isCleared && order.status !== 'Draft' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setReminderDialogOpen(true)}
                                data-testid="ncb-send-reminder-btn"
                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                                <Mail className="h-4 w-4 mr-1" />
                                Send Reminder
                            </Button>
                        )}
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={loadAuditLogs}
                            disabled={loadingLogs}
                        >
                            <History className="h-4 w-4 mr-1" />
                            {loadingLogs ? 'Loading...' : 'Audit Log'}
                        </Button>
                    </div>
                </div>
            )}
            
            {/* Audit Log */}
            {showAuditLog && auditLogs.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-medium text-slate-500 uppercase mb-2">Audit Log</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                        {auditLogs.map((log) => (
                            <div key={log.id} className="text-xs bg-background rounded p-2">
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">
                                        {log.old_status} → {log.new_status}
                                    </span>
                                    <span className="text-muted-foreground">
                                        {new Date(log.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                                <p className="text-muted-foreground">by {log.admin_name}</p>
                                {log.note && <p className="text-slate-600 mt-1">{log.note}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* NCB Reminder preview + edit + send dialog.
                Always mounted alongside the section so both embedded and
                standalone modes get the same send flow. The dialog itself
                is a no-op until ``open`` is true. */}
            <NCBReminderDialog
                open={reminderDialogOpen}
                onOpenChange={setReminderDialogOpen}
                orderId={order.id}
                orderNumber={order.order_number}
                orderTitle={order.title}
                onSent={() => { if (onUpdate) onUpdate(); }}
            />
        </div>
    );

    // If embedded, return just the content
    if (embedded) {
        return content;
    }

    // Otherwise, wrap in a styled div for standalone use
    return (
        <div className="bg-white rounded-lg border border-slate-300 shadow-premium-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Shield className={`h-5 w-5 ${isCleared ? 'text-emerald-500' : 'text-amber-500'}`} />
                    <div>
                        <h3 className="text-sm font-medium text-slate-700">NCB Clearance</h3>
                        <p className="text-xs text-muted-foreground">Rights clearance status for production</p>
                    </div>
                </div>
                <NCBStatusBadge status={ncb.status} />
            </div>
            {content}
        </div>
    );
}

// Customer-facing neutral status component
export function NCBCustomerStatus({ order }) {
    const ncb = order.ncb_clearance || { status: NCB_STATUS.PENDING };
    const isCleared = ncb.status === NCB_STATUS.CLEARED;
    
    // Show neutral message for non-cleared status
    if (isCleared) {
        return (
            <div className="flex items-center gap-2 text-sm text-emerald-600 mb-4" data-testid="ncb-customer-status">
                <CheckCircle className="h-4 w-4" />
                <span>Rights clearance complete</span>
            </div>
        );
    }
    
    return (
        <div className="flex items-center gap-2 text-sm text-amber-600 mb-4" data-testid="ncb-customer-status">
            <Clock className="h-4 w-4" />
            <span>Rights clearance in progress</span>
        </div>
    );
}
